import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { FileLink, filesApi } from '@texas/api/endpoints/filesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { clientEndpoints } from '@texas/clientEndpoints';
import { EmptyDataButton } from '@texas/components/shared/button/EmptyDataButton';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { FileDropzone } from '@texas/components/shared/dropzone/FileDropzone';
import { ImageDropzone } from '@texas/components/shared/dropzone/ImageDropzone';
import { maxSize } from '@texas/components/shared/dropzone/shared';
import { ErrorsList } from '@texas/components/shared/ErrorsList';
import { FileBrowserModal } from '@texas/components/shared/files/FileBrowserModal';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { ServerError } from '@texas/types';
import {
  acceptedImageFormats,
  imageExtensions,
} from '@texas/utils/helpers/filesHelper';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export function ArtworkDropzone({
  compositionGroupId,
  fileId,
  folderId,
  variantId,
}: {
  compositionGroupId: number;
  fileId: number | null;
  folderId: number;
  variantId: number;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    data: file,
    set: setFile,
    refetch,
  } = useApiResource(filesApi.getFileLink);
  const { request: updateRequest } = useApiRequest(
    compositionGroupApi.updateArtworkFile,
  );
  const modal = useDisclosure();
  const fileBrowser = useDisclosure();
  const removeFile = useDisclosure();

  useEffect(() => {
    if (fileId) void refetch(fileId);
    else setFile(null);
  }, [fileId, refetch, setFile]);

  const updateArtwork = useCallback(
    (newFile: FileLink) => {
      request(
        updateRequest,
        [compositionGroupId, newFile.id],
        () => {
          setFile(newFile);
          compositionGroupEvents.onArtworkChanged.dispatch(variantId);
          toast({
            title: t('fileBrowser.updatedFile'),
            status: 'success',
            isClosable: true,
          });
        },
        (error: ServerError) => {
          toast({
            title: t('fileBrowser.updateFileFailed'),
            description: <ErrorsList errors={error.errors} />,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [compositionGroupId, setFile, t, toast, updateRequest, variantId],
  );

  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
  } = useFileUploads(updateArtwork, maxSize);

  return (
    <>
      {fileId ? (
        <Button
          onClick={modal.onOpen}
          variant="texas-light"
          borderRadius="md"
          h="100%"
          w="fit-content"
          leftIcon={<Icons.ImageOutline boxSize={defaultIconSize} />}
        >
          {t('compositionGroup.option.files.viewArtwork')}
        </Button>
      ) : (
        <Flex w="200px">
          <EmptyDataButton
            onClick={modal.onOpen}
            label={t('compositionGroup.option.files.noArtwork')}
            description={t('general.clickToAdd')}
          />
        </Flex>
      )}

      <Modal onClose={modal.onClose} isOpen={modal.isOpen} size="3xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader fontWeight="bold">
            {t('compositionGroup.option.files.artwork')}
          </ModalHeader>
          <ModalBody pb={6}>
            {file ? (
              <ImageDropzone
                imageId={file.id}
                imageIdentifier={file.identifier}
                imageSrc={clientEndpoints.previewImage(file.identifier, 560)}
                imageArchived={file.archived}
                allowEdit={true}
                onUpload={(files) => uploadFiles(files, folderId)}
                isUploading={isUploading}
                uploadFailed={uploadFailed}
                fileUploads={fileUploads}
                removeFileUpload={removeFileUpload}
                onRemoveFileOpen={removeFile.onOpen}
                onFileBrowserOpen={fileBrowser.onOpen}
              />
            ) : (
              <Flex direction="column" gap={4}>
                <Text fontStyle="italic">
                  {t('compositionGroup.option.files.noArtwork')}
                </Text>
                <FileDropzone
                  accept={acceptedImageFormats}
                  isUploading={isUploading}
                  uploadFailed={uploadFailed}
                  fileUploads={fileUploads}
                  removeFileUpload={removeFileUpload}
                  onUpload={(files) => uploadFiles(files, folderId)}
                  onFileBrowserOpen={fileBrowser.onOpen}
                />
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <FileBrowserModal
        rootFolderId={folderId}
        mode="Select"
        isOpen={fileBrowser.isOpen}
        onClose={fileBrowser.onClose}
        extensionFilter={imageExtensions}
        onSelect={(selectedFile: FileLink) => {
          updateArtwork(selectedFile);
          fileBrowser.onClose();
        }}
      />

      <VerifyDialogWithRequest
        headerTitle={t('fileBrowser.removeImage')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={compositionGroupApi.deleteArtworkFile}
        args={[compositionGroupId]}
        isOpen={removeFile.isOpen}
        onClose={removeFile.onClose}
        onPerformed={() => {
          setFile(null);
          compositionGroupEvents.onArtworkChanged.dispatch(variantId);
        }}
        onSuccessTitle={t('fileBrowser.fileRemoved')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
    </>
  );
}
